/**
 * Persists a list view's filter / search / pagination state across navigation.
 *
 * A component opts in by declaring a `filterKeys` array option listing the data
 * properties to remember. On `created` the saved values (scoped per route name)
 * are restored, and a watcher on each key writes changes back to sessionStorage.
 * This keeps a user's selected filters intact when they leave the list to edit an
 * item and then navigate back.
 *
 * Usage:
 *   import persistListFilters from '@/mixins/persist-list-filters';
 *   export default {
 *     mixins: [persistListFilters],
 *     filterKeys: ['searchTerm', 'selectedCourse', 'currentPage'],
 *     ...
 *   };
 */
export default {
  created() {
    if (!this.$options.filterKeys || !this.$options.filterKeys.length) return;
    this.restorePersistedFilters();
    this.$options.filterKeys.forEach(key => {
      this.$watch(key, () => this.savePersistedFilters());
    });
  },

  methods: {
    persistFilterStorageKey() {
      return `lms-filters:${this.$route.name}`;
    },

    restorePersistedFilters() {
      const raw = sessionStorage.getItem(this.persistFilterStorageKey());
      if (!raw) return;
      try {
        const saved = JSON.parse(raw);
        this.$options.filterKeys.forEach(key => {
          if (Object.prototype.hasOwnProperty.call(saved, key)) {
            this[key] = saved[key];
          }
        });
      } catch (e) {
        // Ignore malformed persisted state.
      }
    },

    savePersistedFilters() {
      const data = {};
      this.$options.filterKeys.forEach(key => {
        data[key] = this[key];
      });
      sessionStorage.setItem(this.persistFilterStorageKey(), JSON.stringify(data));
    },
  },
};
